import { useState } from "react";
import { Link } from "../lib/router";
import { api, useData, date, time, eventURL } from "../lib/events";
export function State({ error }: { error: string }) {
  return (
    <div className="notice" role="status">
      {error || "Loading the shows…"}
    </div>
  );
}
export function Shows({ signup = false }: { signup?: boolean }) {
  const { data, error } = useData("/api/events");
  return (
    <>
      <section className="hero">
        <div>
          <p className="eyebrow">BARCELONA · LIVE STAND-UP</p>
          <h1>
            {signup ? (
              <>
                Your mic.
                <br />
                <em>Your moment.</em>
              </>
            ) : (
              <>
                Fresh comedy.
                <br />
                <em>On tap.</em>
              </>
            )}
          </h1>
          <p className="intro">
            {signup
              ? "Pick a show, send your details, and put your name forward. Every lineup starts with a new voice."
              : "Good people, cold drinks, and the next comedian you’ll tell your friends about. Find your next night out."}
          </p>
          <a className="button" href="#shows">
            {signup ? "Find your spot" : "See what’s on"} <span>↗</span>
          </a>
        </div>
        <div className="hero-art">
          <div className="ring">
            <img src="/logo.png" alt="On Tap Comedy logo" />
          </div>
          <span className="stamp">
            SMALL ROOM.
            <br />
            BIG LAUGHS.
          </span>
        </div>
      </section>
      <section id="shows">
        <div className="section-heading">
          <div>
            <p className="eyebrow">
              {signup ? "TAKE THE STAGE" : "THE NEXT ROUND"}
            </p>
            <h2>{signup ? "Shows taking applications" : "What’s on"}</h2>
          </div>
          <span>
            {data
              ? `${data.filter((x: any) => !signup || x.can_apply).length} upcoming shows`
              : ""}
          </span>
        </div>
        {!data ? (
          <State error={error} />
        ) : (
          <div className="show-list">
            {data
              .filter((p: any) => !signup || p.can_apply)
              .map((p: any) => (
                <ShowCard key={p.event.slug} page={p} signup={signup} />
              ))}
            {!data.filter((x: any) => !signup || x.can_apply).length && (
              <div className="empty">
                <h3>
                  {signup
                    ? "The mic is taking a breather."
                    : "More laughs are on the way."}
                </h3>
                <p>
                  {signup
                    ? "No shows are accepting artists right now. Check back for the next opening."
                    : "There are no upcoming shows published yet."}
                </p>
                <Link href="/events">Browse the shows →</Link>
              </div>
            )}
          </div>
        )}
      </section>
      <section className="callout">
        <span className="eyebrow">GOT FIVE MINUTES?</span>
        <h2>We’ve got a mic.</h2>
        <p>
          New material. First-timers. Familiar faces. Apply for the show that
          works for you.
        </p>
        <Link href="/signup" className="button light">
          Get on stage ↗
        </Link>
      </section>
    </>
  );
}
function ShowCard({ page: p, signup }: { page: any; signup: boolean }) {
  const e = p.event,
    d = e.starts_at ? new Date(e.starts_at) : null;
  return (
    <article className="show-card">
      <div className="date-tile">
        <span>
          {d
            ? new Intl.DateTimeFormat("en", {
                month: "short",
                timeZone: e.timezone,
              }).format(d)
            : "SOON"}
        </span>
        <strong>
          {d
            ? new Intl.DateTimeFormat("en", {
                day: "2-digit",
                timeZone: e.timezone,
              }).format(d)
            : "—"}
        </strong>
      </div>
      <div className="show-info">
        <div className="tags">
          <span className={"tag " + (p.status === "cancelled" ? "red" : "")}>
            {p.status === "cancelled"
              ? "Cancelled"
              : p.can_apply
                ? "Artist applications open"
                : p.status === "closed"
                  ? "Closed"
                  : "Live comedy"}
          </span>
        </div>
        <Link href={eventURL(e.slug)} className="show-title">
          {e.title}
        </Link>
        <p>
          {date(e.starts_at, e.timezone)} ·{" "}
          {p.venue?.name || "Venue to be announced"}
        </p>
      </div>
      <Link
        href={eventURL(e.slug) + (signup ? "/apply" : "")}
        className="button secondary"
      >
        {signup ? "Apply for a spot" : "View show"} ↗
      </Link>
    </article>
  );
}
export function Show({ slug }: { slug: string }) {
  const { data: p, error } = useData("/api/events/" + slug);
  if (!p) return <State error={error} />;
  const e = p.event;
  return (
    <>
      <Link href="/events" className="back">
        ← All shows
      </Link>
      <section className="show-hero">
        <p className="eyebrow">
          ON TAP COMEDY ·{" "}
          {p.status === "cancelled" ? "CANCELLED" : "LIVE IN BARCELONA"}
        </p>
        <h1>{e.title}</h1>
        <p className="intro">
          {date(e.starts_at, e.timezone)}
          {e.ends_at ? " – " + time(e.ends_at, e.timezone) : ""}
        </p>
        <p>
          {p.venue?.name} {p.venue?.address && "· " + p.venue.address}{" "}
          {p.venue?.city}
        </p>
      </section>
      {p.status === "cancelled" && (
        <div className="notice error">
          <strong>This show has been cancelled.</strong>
          <p>
            {p.settings.cancellation_reason ||
              "Please check the other shows for another night out."}
          </p>
        </div>
      )}
      <div className="detail-grid">
        <section>
          <p className="eyebrow">THE NIGHT</p>
          <h2>A room full of laughs.</h2>
          <p className="prose">
            {e.description || "Show details will be announced soon."}
          </p>
          {e.external_checkout_url && p.status === "published" && (
            <a
              href={e.external_checkout_url}
              className="button"
              target="_blank"
              rel="noreferrer"
            >
              Get audience tickets ↗
            </a>
          )}
          <div className="lineup">
            <p className="eyebrow">ON THE MIC</p>
            <h2>The lineup</h2>
            {p.schedule.length ? (
              <div className="artist-grid">
                {p.schedule.map((s: any, i: number) => (
                  <div className="artist" key={i}>
                    {s.photo_url ? (
                      <img
                        src={s.photo_url.replace("/public/", "/api/events/")}
                        alt={s.performer_name}
                      />
                    ) : (
                      <div className="artist-initial">
                        {s.performer_name[0]}
                      </div>
                    )}
                    <strong>{s.performer_name}</strong>
                    <span>
                      {s.starts_at
                        ? time(s.starts_at, e.timezone)
                        : "On the lineup"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p>
                {p.status === "cancelled"
                  ? "This show will not take place."
                  : "The lineup is being curated. Check back to meet the performers."}
              </p>
            )}
          </div>
        </section>
        <aside className="apply-card">
          <p className="eyebrow">FOR THE COMEDIANS</p>
          <h2>
            Your spot
            <br />
            on stage.
          </h2>
          <dl>
            <div>
              <dt>Set length</dt>
              <dd>{p.settings.set_minutes} minutes</dd>
            </div>
            {p.settings.performer_capacity > 0 && (
              <div>
                <dt>Lineup spots</dt>
                <dd>{p.settings.performer_capacity}</dd>
              </div>
            )}
            {p.settings.closes_at && (
              <div>
                <dt>Apply before</dt>
                <dd>{date(p.settings.closes_at, e.timezone)}</dd>
              </div>
            )}
          </dl>
          <p className="prose">{p.settings.instructions}</p>
          {p.can_apply ? (
            <Link href={eventURL(slug) + "/apply"} className="button">
              Apply for this show ↗
            </Link>
          ) : (
            <div className="notice">{p.application_message}</div>
          )}
          <small>
            Applying doesn’t confirm a spot. The organiser will contact selected
            artists.
          </small>
        </aside>
      </div>
    </>
  );
}
export function Apply({ slug }: { slug: string }) {
  const { data: p, error } = useData("/api/events/" + slug),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [done, setDone] = useState(false),
    [photo, setPhoto] = useState<File | null>(null);
  if (!p) return <State error={error} />;
  const e = p.event;
  async function submit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const values: any = Object.fromEntries(new FormData(ev.currentTarget));
      values.photo_consent = values.photo_consent === "on";
      if (photo) {
        if (photo.size > 2 * 1024 * 1024)
          throw Error("Please choose a photo under 2 MB.");
        values.photo_base64 = await new Promise<string>((yes, no) => {
          const r = new FileReader();
          r.onload = () => yes(String(r.result).split(",")[1]);
          r.onerror = no;
          r.readAsDataURL(photo);
        });
      }
      await api("/api/events/" + slug + "/apply", "POST", values);
      setDone(true);
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="form-page">
      <Link href={eventURL(slug)} className="back">
        ← Show details
      </Link>
      <p className="eyebrow">GET ON STAGE</p>
      <h1>{done ? "You’re in the running." : "Put your name down."}</h1>
      <div className="event-summary">
        <strong>{e.title}</strong>
        <span>
          {date(e.starts_at, e.timezone)} · {p.venue?.name}
        </span>
      </div>
      {done ? (
        <div className="success" role="status">
          <h2>Application received.</h2>
          <p>
            Your spot isn’t confirmed yet. The organiser will review your
            application and contact you using the details you provided.
          </p>
          <Link href="/events" className="button">
            Back to the shows →
          </Link>
        </div>
      ) : !p.can_apply ? (
        <div className="notice">
          {p.application_message}
          <p>
            <Link href="/signup">Find another show →</Link>
          </p>
        </div>
      ) : (
        <form onSubmit={submit}>
          <p className="prose">{p.settings.instructions}</p>
          {p.settings.closes_at && (
            <p className="hint">
              Deadline: {date(p.settings.closes_at, e.timezone)}
            </p>
          )}
          <div className="form-grid">
            <label>
              Your name
              <input
                name="applicant_name"
                autoComplete="name"
                maxLength={160}
                required
              />
            </label>
            <label>
              Stage name <span>(optional)</span>
              <input name="stage_name" maxLength={160} />
            </label>
            <label>
              Instagram
              <input
                name="instagram"
                placeholder="@yourhandle"
                maxLength={31}
              />
            </label>
            <label>
              WhatsApp
              <input
                name="phone"
                type="tel"
                placeholder="+34…"
                autoComplete="tel"
              />
            </label>
            <label className="full">
              Email {p.settings.email_required ? "" : "(optional)"}
              <input
                name="email"
                type="email"
                required={p.settings.email_required}
                autoComplete="email"
              />
            </label>
          </div>
          <p className="hint">
            Include at least one way to reach you: Instagram, WhatsApp, or
            email.
          </p>
          <label>
            A little about your act <span>(optional)</span>
            <textarea name="bio" maxLength={4000} rows={3} />
          </label>
          <label>
            Anything we should know? <span>(optional)</span>
            <textarea name="tech_needs" maxLength={4000} rows={2} />
          </label>
          <label>
            Artist photo <span>(optional · JPEG or PNG · max 2 MB)</span>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(ev) => setPhoto(ev.target.files?.[0] || null)}
            />
          </label>
          {photo && (
            <label className="check">
              <input name="photo_consent" type="checkbox" required />I allow the
              organiser to use this photo for this show’s lineup and promotion.
              It stays private until I’m selected and the lineup is published.
            </label>
          )}
          <p className="hint">
            Your contact details are shared only with the organiser to manage
            this application.
          </p>
          {message && (
            <div className="notice error" role="alert">
              {message}
            </div>
          )}
          <button className="button" disabled={busy}>
            {busy ? "Sending your application…" : "Apply for this show ↗"}
          </button>
          <p className="hint">
            This is an application, not a confirmed booking.
          </p>
        </form>
      )}
    </div>
  );
}
